import type { ComputedRef } from 'vue'
import { computed, reactive } from 'vue'

export interface QueuedAction<T = void> {
  id: string
  run: () => Promise<T>
  resolve: (value: T) => void
  reject: (reason?: unknown) => void
  promise: Promise<T>
  status: 'pending' | 'completed' | 'error'
  error?: Error
  timestamp: number
}

export interface RequestQueueState {
  actions: Map<string, QueuedAction<any>>
  processing: boolean
  currentAction: string | null
}

export interface RequestQueueResult {
  isProcessing: ComputedRef<boolean>
  pendingCount: ComputedRef<number>
  currentAction: ComputedRef<QueuedAction | undefined>
  enqueue: <T>(action: () => Promise<T>) => Promise<T>
  clear: () => void
  retry: (actionId: string) => Promise<void>
}

export function createRequestQueue() {
  // State management
  const state = reactive<RequestQueueState>({
    actions: new Map(),
    processing: false,
    currentAction: null,
  })

  // Generate unique IDs for actions
  const generateActionId = () => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  // Process the next action in queue. Actions are stored (not started) on
  // enqueue and only invoked here, strictly one at a time — so concurrent
  // enqueues never run their side effects in parallel. This matters for order
  // mutations whose backend transactions would otherwise race: e.g. the default
  // payment selection's setIntermediatePaymentMethod racing the user's choice
  // and intermittently dropping the cash-on-delivery surcharge.
  const processNext = async () => {
    if (state.processing || state.actions.size === 0)
      return

    state.processing = true
    const [nextActionId, nextAction] = Array.from(state.actions.entries())[0]!
    state.currentAction = nextActionId

    try {
      const result = await nextAction.run()
      nextAction.status = 'completed'
      nextAction.resolve(result)
    }
    catch (error) {
      nextAction.status = 'error'
      nextAction.error = error as Error
      nextAction.reject(error)
    }
    finally {
      // Always remove the finished action (success or error); leaving an errored
      // action in the map would make processNext re-pick it forever.
      state.actions.delete(nextActionId)
      state.processing = false
      state.currentAction = null
      if (state.actions.size > 0)
        processNext()
    }
  }

  const enqueue = async <T>(actionFn: () => Promise<T>): Promise<T> => {
    const actionId = generateActionId()

    // Defer execution: capture resolve/reject and let processNext invoke the
    // action when it reaches the front of the queue, so actions run serially.
    let resolve!: (value: T) => void
    let reject!: (reason?: unknown) => void
    const actionPromise = new Promise<T>((res, rej) => {
      resolve = res
      reject = rej
    })

    const queuedAction: QueuedAction<T> = {
      id: actionId,
      run: actionFn,
      resolve,
      reject,
      promise: actionPromise,
      status: 'pending',
      timestamp: Date.now(),
    }

    state.actions.set(actionId, queuedAction)

    // Start processing if not already processing
    if (!state.processing) {
      processNext()
    }

    return actionPromise
  }

  const retry = async (actionId: string) => {
    const action = state.actions.get(actionId)
    if (!action || action.status !== 'error')
      return

    state.actions.delete(actionId)
    if (!state.processing) {
      processNext()
    }
  }

  const clear = () => {
    state.actions.clear()
    state.processing = false
    state.currentAction = null
  }

  return {
    isProcessing: computed(() => state.processing),
    pendingCount: computed(() => state.actions.size),
    currentAction: computed(() => state.currentAction ? state.actions.get(state.currentAction) : undefined),
    enqueue,
    clear,
    retry,
  }
}

let globalQueue: RequestQueueResult | null = null

export function useRequestQueue(): RequestQueueResult {
  if (!globalQueue) {
    globalQueue = createRequestQueue()
  }
  return globalQueue
}
